/**
 * Website addresses and their language (SEO, 2026-10-27). Dutch pages live at the root (/prijzen), the English
 * versions of the same pages under /en (/en/prijzen); `/` and `/en` are the two home pages. The language of a page
 * comes from its address, so search engines see one fixed language per URL (hreflang). Pure functions: also used by
 * the session (guest language), the prerender script and the server.
 */
export type SiteLang = 'nl' | 'en';

export const SITE_ORIGIN = 'https://btwkwartier.nl';

export type PageKey =
  | 'home'
  | 'product'
  | 'how'
  | 'forWhom'
  | 'pricing'
  | 'faq'
  | 'example'
  | 'trust'
  | 'about'
  | 'contact'
  | 'check'
  | 'privacy'
  | 'terms'
  | 'subprocessors'
  | 'dpa'
  | 'help';

export interface IndexedPage {
  key: PageKey;
  /** Dutch path (the English one is enPath(path)). */
  path: string;
  /** Help article slug (key 'help'). */
  slug?: string;
  priority: number;
}

export const HELP_SLUGS = ['documenten-voorbereiden', 'documenten-toevoegen', 'bedrag-terugvinden', 'btw-concept-controleren'] as const;

/** Every page that search engines may index, in sitemap order. */
export const INDEXED_PAGES: IndexedPage[] = [
  { key: 'home', path: '/', priority: 1 },
  { key: 'product', path: '/product', priority: 0.9 },
  { key: 'how', path: '/zo-werkt-het', priority: 0.9 },
  { key: 'pricing', path: '/prijzen', priority: 0.9 },
  { key: 'forWhom', path: '/voor-wie', priority: 0.8 },
  { key: 'check', path: '/btw-check', priority: 0.8 },
  { key: 'faq', path: '/veelgestelde-vragen', priority: 0.8 },
  { key: 'example', path: '/voorbeeld', priority: 0.7 },
  { key: 'trust', path: '/vertrouwen', priority: 0.7 },
  { key: 'about', path: '/over-ons', priority: 0.6 },
  { key: 'contact', path: '/contact', priority: 0.6 },
  ...HELP_SLUGS.map((slug) => ({ key: 'help' as const, path: `/hulp/${slug}`, slug, priority: 0.6 })),
  { key: 'privacy', path: '/privacy', priority: 0.3 },
  { key: 'terms', path: '/voorwaarden', priority: 0.3 },
  { key: 'subprocessors', path: '/subverwerkers', priority: 0.2 },
  { key: 'dpa', path: '/verwerkersovereenkomst', priority: 0.2 },
];

/** Website pages that exist in both languages but are not indexed (thank-you page). */
const OTHER_BILINGUAL = ['/aanvraag-ontvangen'];

const BILINGUAL = new Set([...INDEXED_PAGES.map((p) => p.path), ...OTHER_BILINGUAL]);

export function enPath(nlPath: string): string {
  return nlPath === '/' ? '/en' : `/en${nlPath}`;
}

export function pathFor(nlPath: string, lang: SiteLang): string {
  return lang === 'en' ? enPath(nlPath) : nlPath;
}

function clean(pathname: string): string {
  const p = pathname.split(/[?#]/)[0] || '/';
  return p.length > 1 ? p.replace(/\/+$/, '') : p;
}

/**
 * Language and Dutch base path of an address. `lang` is null for addresses that are not bilingual website pages
 * (login, the app…): those follow the visitor's chosen language as before.
 */
export function pathInfo(pathname: string): { lang: SiteLang | null; base: string; page: IndexedPage | null } {
  const p = clean(pathname);
  if (p === '/en' || p.startsWith('/en/')) {
    const base = p === '/en' ? '/' : p.slice(3);
    return BILINGUAL.has(base) ? { lang: 'en', base, page: INDEXED_PAGES.find((x) => x.path === base) ?? null } : { lang: null, base: p, page: null };
  }
  return BILINGUAL.has(p) ? { lang: 'nl', base: p, page: INDEXED_PAGES.find((x) => x.path === p) ?? null } : { lang: null, base: p, page: null };
}

/** A link target in the current website language: /prijzen → /en/prijzen on the English site; other targets unchanged. */
export function localizeTo(to: string, lang: SiteLang): string {
  if (lang !== 'en' || !to.startsWith('/')) return to;
  const i = to.search(/[?#]/);
  const path = i < 0 ? to : to.slice(0, i);
  const rest = i < 0 ? '' : to.slice(i);
  if (path === '/en' || path.startsWith('/en/')) return to;
  return BILINGUAL.has(clean(path)) ? enPath(clean(path)) + rest : to;
}
